import { useEffect, useRef, useState, useCallback } from "react";
import logger from "shared/utils/logging";

const Logger = logger;


export const useMediaManager = () => {
  const [mediaAllowed, setMediaAllowed] = useState(false);
  const [availableCameras, setAvailableCameras] = useState(null);
  const [availableMicrophones, setAvailableMicrophones] = useState(null);
  const [mediaError, setMediaError] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);

  const refMediaStream = useRef(null);
  const refAudioContext = useRef(null);
  const refOscillator = useRef(null);
  const refSilentAudioTrack = useRef(null);
  const refAudioGainNode = useRef(null);
  const refBlackVideoInterval = useRef(null);

  // Update both ref and state synchronously
  const updateMediaStream = useCallback((stream) => {
    // Stop previous stream if exists
    if (refMediaStream.current) {
      refMediaStream.current.getTracks().forEach((track) => track.stop());
    }
    refMediaStream.current = stream;
    setMediaStream(stream);
  }, []);

  const stopMediaStream = useCallback(() => {
    if (refMediaStream.current) {
      refMediaStream.current.getTracks().forEach((track) => track.stop());
      refMediaStream.current = null;
      setMediaStream(null);
    }
  }, []);

  // Initialize AudioContext lazily
  const getAudioContext = useCallback(() => {
    if (!refAudioContext.current) {
      refAudioContext.current = new AudioContext();
    }
    return refAudioContext.current;
  }, []);

  // Cleanup function for audio context and streams
  const cleanup = useCallback(() => {
    console.log("useMediaManager: cleanup");
    stopMediaStream();
    stopSilentAudioTrack();

    // Clear black video interval
    if (refBlackVideoInterval.current) {
      clearInterval(refBlackVideoInterval.current);
      refBlackVideoInterval.current = null;
    }

    // Close audio context
    if (refAudioContext.current && refAudioContext.current.state !== "closed") {
      refAudioContext.current.close();
      refAudioContext.current = null;
    }
  }, [stopMediaStream]);

  useEffect(() => {
    if (checkWebRTCPermissions()) {
      setMediaAllowed(true);
      // init devices list
      updateAvailableDevices();
      trackDeviceChange();
    } else {
      setMediaAllowed(false);
    }

    return cleanup;
  }, [cleanup]);

  const getMediaInputDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(
        (device) => device.kind === "audioinput" || device.kind === "videoinput"
      );
    } catch (err) {
      Logger.error(
        "Cannot get devices -> error name: " + err.name + ": " + err.message
      );
      setMediaError("Failed to enumerate media devices");
      throw err;
    }
  }, []);

  const updateAvailableDevices = useCallback(async () => {
    try {
      const devices = await getMediaInputDevices();
      const microphones = devices.filter(
        (device) => device.kind === "audioinput"
      );
      const cameras = devices.filter((device) => device.kind === "videoinput");

      setAvailableCameras(cameras);
      setAvailableMicrophones(microphones);
    } catch (err) {
      console.error("Failed to update available devices:", err);
    }
  }, [getMediaInputDevices]);

  const trackDeviceChange = useCallback(() => {
    const handleDeviceChange = () => {
      console.log("device changed");
      updateAvailableDevices();
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    // Return cleanup function
    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [updateAvailableDevices]);

  const checkWebRTCPermissions = useCallback(() => {
    if (typeof navigator.mediaDevices === "undefined") {
      const errorMsg =
        "Cannot open camera and mic because of unsecure context. Please Install SSL(https)";
      Logger.debug(errorMsg);
      setMediaError(errorMsg);
      setMediaAllowed(false);
      return false;
    }

    if (!navigator.mediaDevices) {
      const errorMsg = "Get mediaDevices is not allowed";
      Logger.debug("getUserMediaIsNotAllowed");
      setMediaError(errorMsg);
      setMediaAllowed(false);
      return false;
    }
    return true;
  }, []);

  const startMediaStream = useCallback(
    async (mediaConstraint) => {
      setMediaError(null);
      setMediaAllowed(true);

      if (!mediaConstraint.audio && !mediaConstraint.video) {
        updateMediaStream(null);
        return null;
      }

      if (typeof navigator.mediaDevices === "undefined") {
        const errorMsg =
          "Cannot open media devices because of unsecure context. Please Install SSL(https)";
        Logger.debug(errorMsg);
        setMediaError(errorMsg);
        setMediaAllowed(false);
        throw new Error(errorMsg);
      }

      if (!navigator.mediaDevices) {
        const errorMsg = "Media devices not found!";
        console.log("Media devices not found", navigator.mediaDevices);
        setMediaError(errorMsg);
        setMediaAllowed(false);
        throw new Error(errorMsg);
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia(
          mediaConstraint
        );
        updateMediaStream(stream);
        return stream;
      } catch (err) {
        if (err instanceof DOMException) {
          if (
            err.name === "PermissionDeniedError" ||
            err.name === "NotAllowedError"
          ) {
            setMediaError("Permission denied for microphone!");
            setMediaAllowed(false);
          } else {
            setMediaError(err.message);
          }
        } else {
          setMediaError(err.message);
        }
        throw err;
      }
    },
    [updateMediaStream]
  );

  const muteAudioTrack = useCallback(() => {
    if (refMediaStream.current) {
      refMediaStream.current.getAudioTracks().forEach((track) => {
        track.enabled = false;
        console.log("Audio track muted: ", track);
      });
    }
  }, []);

  const unmuteAudio = useCallback(() => {
    if (refMediaStream.current) {
      refMediaStream.current.getAudioTracks().forEach((track) => {
        track.enabled = true;
        console.log("Audio track unmuted: ", track);
      });
    }
  }, []);

  const getSilentAudioTrack = useCallback(() => {
    stopSilentAudioTrack();
    const audioContext = getAudioContext();

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    refOscillator.current = oscillator;
    refAudioGainNode.current = gainNode;

    oscillator.connect(gainNode);

    const mediaStreamDestination = audioContext.createMediaStreamDestination();
    gainNode.connect(mediaStreamDestination);

    oscillator.start();

    oscillator.onended = function () {
      console.log("Your tone has now stopped playing!");
    };

    const initialVol = 0.01;
    gainNode.gain.value = initialVol;
    gainNode.gain.minValue = initialVol;
    gainNode.gain.maxValue = initialVol;

    const silentAudioTrack = mediaStreamDestination.stream.getAudioTracks()[0];
    refSilentAudioTrack.current = silentAudioTrack;
    return silentAudioTrack;
  }, [getAudioContext]);

  const stopSilentAudioTrack = useCallback(() => {
    if (refOscillator.current) {
      const oscillator = refOscillator.current;
      oscillator.stop();
      oscillator.disconnect();
      refOscillator.current = null;
    }

    if (refSilentAudioTrack.current) {
      const silentAudioTrack = refSilentAudioTrack.current;
      silentAudioTrack.stop();
      refSilentAudioTrack.current = null;
    }
  }, []);

  const getBlackVideoTrack = useCallback(
    ({ width = 320, height = 240 } = {}) => {
      const canvas = Object.assign(document.createElement("canvas"), {
        width,
        height,
      });
      const ctx = canvas.getContext("2d");
      const stream = canvas.captureStream();

      // Clear any existing interval
      if (refBlackVideoInterval.current) {
        clearInterval(refBlackVideoInterval.current);
      }

      // We need to send black frames within a time interval, because when the user turn off the camera,
      // player can't connect to the sender since there is no data flowing.
      // Sending a black frame in each 3 seconds resolves it.
      refBlackVideoInterval.current = setInterval(() => {
        ctx.fillRect(0, 0, width, height);
      }, 3000);

      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = (evt) => {
          if (refBlackVideoInterval.current) {
            clearInterval(refBlackVideoInterval.current);
            refBlackVideoInterval.current = null;
          }
        };
      }

      return stream;
    },
    []
  );

  return {
    mediaAllowed,
    mediaError,
    mediaStream,
    availableCameras,
    availableMicrophones,
    getMediaStream: startMediaStream,
    muteAudioTrack,
    unmuteAudio,
    stopMediaStream,
    getSilentAudioTrack,
    getBlackVideoTrack,
  };
};
